/**
 * Formats the value in form of (111) 111-1111
 * @param {boolean} useSymbol - format with formatphonenumber symbol
 * @returns {Number}
 */
export const formatSSN = (ssn) => {
    if(!ssn){
        return "";
    }
    const raw = String(ssn).replace(/\D/g, "").slice(-9);
    const countryRaw =
    (typeof window !== "undefined" && (localStorage.getItem("ssnCountry") || localStorage.getItem("ssnFormat") || localStorage.getItem("nationalIdCountry"))) || "US";
    const country = String(countryRaw).toUpperCase();
    const pattern = country === "CA" || country === "CAN" || country === "CANADA" ? [3, 6, 9] : [3, 5, 9];
    if (raw.length < pattern[2]) return "";
    return `${raw.slice(0, pattern[0])}-${raw.slice(pattern[0], pattern[1])}-${raw.slice(pattern[1])}`
}

/**
 * Formats the SSN value according to visibility and country
 * @param {string, pattern} useSymbol - format with regex
 * @returns {string}
 */
export const formatSSNVisibility = (ssn, pattern, showNationalID = null, fullyMasked = false) => {
    const ssnValue = ssn ?? '';

    // Use passed configuration first, then fallback to localStorage
    let visibilityConfig = showNationalID;
    
    if (visibilityConfig === null) {
        const visRaw = 
        (typeof window !== "undefined") && 
        (localStorage.getItem("ssnVisibilityMode") || 
         localStorage.getItem("ssnVisiblitiyMode") ||
         localStorage.getItem("showNationalID"));
        visibilityConfig = visRaw;
    }
    
    // Normalize effective visibility mode
    // Priority: ssnVisibilityMode (FULL | MASKED | NONE)
    // Fallback: boolean legacy (showNationalID)
    const normalizeVisibility = (cfg) => {
        if (cfg === true || String(cfg).toLowerCase().trim() === 'true') return 'FULL';
        if (cfg === false || String(cfg).toLowerCase().trim() === 'false') return 'NONE';
      
        const s = String(cfg || '').toUpperCase().trim();
      
        if (s === 'FULL' || s === 'MASKED' || s === 'NONE') return s;
      
        if (['Y', 'YES', '1', 'T'].includes(s)) return 'FULL';
        if (['N', 'NO', '0', 'F'].includes(s)) return 'NONE';  
      
        return 'MASKED';
      };

    // Determine country (US or CA)
    const countryRaw =
    (typeof window !== "undefined" && (localStorage.getItem("ssnCountry") || localStorage.getItem("ssnFormat") || localStorage.getItem("nationalIdCountry"))) || "US";
    const country = String(countryRaw).toUpperCase();
    const isCanada = (country === "CA" || country === "CAN" || country === "CANADA");
    const defaultMaskedValue = isCanada ? `XXX-XXX-XXX` : `XXX-XX-XXXX`;
    const hasMaskCharacters = /[*X]/i.test(String(ssnValue));

    const buildMaskFromPattern = (maskChar = 'X') => {
        if (pattern && pattern.length >= 3) {
            const segment0 = maskChar.repeat(pattern[0]);
            const segment1 = maskChar.repeat(pattern[1] - pattern[0]);
            const segment2 = maskChar.repeat(pattern[2] - pattern[1]);
            return `${segment0}-${segment1}-${segment2}`;
        }
        return defaultMaskedValue;
    };

    // Resolve effective visibility
    const effective = normalizeVisibility(visibilityConfig);

    // NONE or fully masked columns: always show a fully masked value
    if (fullyMasked || effective === "NONE") {
        return buildMaskFromPattern();
    }

    // No underlying value to show and no mask override
    if (!ssnValue) {
        return "";
    }

    // Apply explicit pattern formatting when we have a raw value and pattern
    if (pattern && pattern.length > 0 && String(ssnValue).length > 8) {
        const removeHiphens = ssnValue.includes('-') ? ssnValue.replace(/-/g, '') : ssnValue;
        const filteredNumber = ('' + removeHiphens).replace(/[\D*]/g, '');
        const formatedSSN = filteredNumber.substring(0, pattern[0]) + '-' + filteredNumber.substring(pattern[0], pattern[1]) + '-' + filteredNumber.substring(pattern[1], pattern[2]);
        return formatedSSN;
    }

    // FULL: show full SSN formatted by country
    if (effective === "FULL") {
        const formatted = formatSSN(ssnValue);
        if (formatted) {
            return formatted;
        }
        if (hasMaskCharacters) {
            return buildMaskFromPattern();
        }
        return String(ssnValue);
    }
    
    // MASKED: partially masked format (US last 3, CA last 3)
    const raw = String(ssnValue).replace(/\D/g, "").slice(-9);
    if (!raw) {
        if (hasMaskCharacters) {
            return buildMaskFromPattern();
        }
        return "";
    }

    if (isCanada) {
        // Canada format: XXX-XXX-XXX -> XXX-XXX-123 (last 3 digits)
        const last3 = raw.slice(-3);
        return `XXX-XXX-${last3}`;
    } else {
        // US format: XXX-XX-XXXX -> XXX-XX-123 (last 3 digits)
        const last3 = raw.slice(-3);
        return `XXX-XX-${last3}`;
    }

}

/**
 * Converts (111) 111-1111 to 1111111111
 * @param {string} useSymbol - format with formPhoneNumber symbol
 * @returns {String}
 */
export const formSSNNumber = (value) => {
    return ((value).replace(/\D/g, '')).toString();
}

/**
 * Wrapper for default usage of SSN visibility
 * @param {string} ssn
 * @returns {string}
 */
export const formatSSNWithVisibility = (ssn) => {
    return formatSSNVisibility(ssn, []);
}
